import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoSettingsOutline, IoMenu, IoClose, IoHomeOutline, IoSchoolOutline, IoLibraryOutline, IoChatbubblesOutline, IoSparklesOutline, IoJournalOutline, IoVolumeHigh, IoVolumeMute } from "react-icons/io5";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { TextSizeButton } from "@/components/TextSizeButton";
import NerodusGuide from "@/components/NerodusGuide";
import AccessRequestPrompt from "@/components/AccessRequestPrompt";
import ReviewAlertPrompt from "@/components/ReviewAlertPrompt";
import { getHubConfig, getCarnetPending, patchUnreadCount } from "@/lib/api";

export const RED = "var(--brand)";
const DISCORD_INVITE = "https://discord.gg/sFCKWpgRg";

function useIsMobile() {
  const [m, setM] = useState(typeof window !== "undefined" && window.matchMedia("(max-width: 860px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 860px)");
    const on = () => setM(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);
  return m;
}

/**
 * DojoShell — coquille de navigation Tengoku Dōjō (thème blanc/noir/rouge).
 * Desktop : barre du haut complète. Mobile : logo + bouton menu (drawer).
 */
export default function DojoShell({ crumbs = [], children, bare = false }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { muted, toggleMute } = useApp();
  const isAdmin = user?.role === "admin";
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);

  // Point rouge "nouveau" : signatures des nouveautés du Hub par section (tag).
  const [newsSig, setNewsSig] = useState({});
  const [seenTick, setSeenTick] = useState(0);
  const SECTION_PATH = { ecole: "/ecole", biblio: "/bibliotheque", communaute: "/communaute", accompagnement: "/accompagnement" };
  useEffect(() => {
    getHubConfig().then((c) => {
      const m = {};
      (c?.news || []).forEach((n) => { if (n.tag) m[n.tag] = `${m[n.tag] || ""}|${n.title || ""}`; });
      setNewsSig(m);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    const sec = Object.keys(SECTION_PATH).find((k) => loc.pathname.startsWith(SECTION_PATH[k]));
    if (sec && newsSig[sec]) { localStorage.setItem(`td_news_seen_${sec}`, newsSig[sec]); setSeenTick((t) => t + 1); }
    // eslint-disable-next-line
  }, [loc.pathname, newsSig]);
  const unseen = (sec) => !!newsSig[sec] && (typeof window !== "undefined") && localStorage.getItem(`td_news_seen_${sec}`) !== newsSig[sec] && seenTick >= 0;
  const NAV_SECTION = { "École": "ecole", "Bibliothèque": "biblio", "Communauté": "communaute", "Accompagnement": "accompagnement" };
  const Dot = () => <span style={styles.newsDot} data-testid="news-dot" />;

  const go = (path) => { setMenuOpen(false); nav(path); };
  const goDiscord = () => { setMenuOpen(false); nav("/communaute"); };

  // Relectures en attente (admin) → pastille
  const [pending, setPending] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    getCarnetPending().then((r) => setPending(r.pending || 0)).catch(() => {});
  }, [isAdmin, loc.pathname]);

  // Nouveautés non lues → pastille
  const [patchUnread, setPatchUnread] = useState(0);
  useEffect(() => {
    if (!isAuthenticated) return;
    patchUnreadCount().then((r) => setPatchUnread(r.count || 0)).catch(() => {});
  }, [isAuthenticated, loc.pathname]);

  const Avatar = ({ size = 34 }) => (
    user?.avatar
      ? <img src={user.avatar} alt="" style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: "2px solid var(--brand)" }} />
      : <span style={{ width: size, height: size, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--brand)", color: "#fff", fontWeight: 800, fontSize: size * 0.42, flexShrink: 0 }}>{(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}</span>
  );

  const NAV = [
    { label: "Hub", action: () => go("/hub") },
    { label: "École", action: () => go("/ecole") },
    { label: "Bibliothèque", action: () => go("/bibliotheque") },
    { label: "Mon Carnet", action: () => go("/carnet") },
    { label: "Accompagnement", action: () => go("/accompagnement") },
    { label: "Communauté", action: goDiscord },
    { label: "Membres", action: () => go("/membres") },
    { label: "Nouveautés", action: () => go("/nouveautes") },
    ...(isAdmin ? [{ label: "Relectures", action: () => go("/carnet/eleves") }, { label: "Mes accompagnés", action: () => go("/accompagnes") }] : []),
    { label: "Paramètres", action: () => go("/parametres") },
  ];

  return (
    <div style={styles.page}>
      {!isMobile && <div style={styles.bgKanji} aria-hidden>道</div>}
      <header style={{ ...styles.header, ...(isMobile ? { background: "var(--bg-primary)", backdropFilter: "none", WebkitBackdropFilter: "none" } : {}) }}>
        <button style={styles.brand} onClick={() => nav("/hub")} title="Retour au Hub">
          <span style={styles.kanji}>天</span>
          <span style={styles.brandText}>TENGOKU <span style={{ color: RED }}>DŌJŌ</span></span>
        </button>

        {!bare && !isMobile && (
          <nav style={styles.crumbs}>
            <button style={styles.crumbHome} onClick={() => nav("/hub")}>Hub</button>
            {crumbs.map((c, i) => (
              <span key={i} style={styles.crumbItem}>
                <span style={styles.sep}>›</span>
                {c.to ? (
                  <button style={styles.crumbLink} onClick={() => nav(c.to)}>{c.label}</button>
                ) : (
                  <span style={styles.crumbCurrent}>{c.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}

        {isMobile ? (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <button style={{ ...styles.gear, color: muted ? "var(--text-secondary)" : "var(--brand)" }} onClick={toggleMute} title={muted ? "Activer la musique" : "Couper la musique"} data-testid="sound-toggle-mobile">{muted ? <IoVolumeMute size={18} /> : <IoVolumeHigh size={18} />}</button>
            {isAuthenticated && (
              <button style={styles.avatarBtn} data-testid="mobile-avatar" onClick={() => nav("/profil")} aria-label="Profil"><Avatar size={36} /></button>
            )}
            <button style={styles.menuBtn} data-testid="mobile-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Menu">
              <IoMenu size={20} /> <span style={styles.menuBtnLabel}>Menu</span>
            </button>
          </div>
        ) : (
          <div style={styles.right}>
            <button style={{ ...styles.navLink, position: "relative" }} data-testid="nav-ecole" onClick={() => nav("/ecole")}>École{unseen("ecole") && <Dot />}</button>
            <button style={{ ...styles.navLink, position: "relative" }} data-testid="nav-bibliotheque" onClick={() => nav("/bibliotheque")}>Bibliothèque{unseen("biblio") && <Dot />}</button>
            <button style={styles.navLink} data-testid="nav-carnet" onClick={() => nav("/carnet")}>Mon Carnet</button>
            <button style={{ ...styles.navLink, position: "relative" }} data-testid="nav-accompagnement" onClick={() => nav("/accompagnement")}>Accompagnement{unseen("accompagnement") && <Dot />}</button>
            <button style={{ ...styles.navLink, position: "relative" }} data-testid="nav-communaute" onClick={() => nav("/communaute")}>Communauté{unseen("communaute") && <Dot />}</button>
            <button style={styles.navLink} data-testid="nav-membres" onClick={() => nav("/membres")}>Membres</button>
            <button style={{ ...styles.navLink, position: "relative" }} data-testid="nav-nouveautes" onClick={() => nav("/nouveautes")}>Nouveautés{patchUnread > 0 && <Dot />}</button>
            {isAdmin && <button style={{ ...styles.navLink, position: "relative" }} data-testid="nav-carnets-eleves" onClick={() => nav("/carnet/eleves")}>Relectures{pending > 0 && <Dot />}</button>}
            {isAdmin && <button style={styles.navLink} data-testid="nav-accompagnes" onClick={() => nav("/accompagnes")}>Accompagnés</button>}
            <TextSizeButton btnStyle={{ ...styles.gear, padding: 0 }} />
            <button style={{ ...styles.gear, color: muted ? "var(--text-secondary)" : "var(--brand)" }} onClick={toggleMute} title={muted ? "Activer la musique" : "Couper la musique"} data-testid="sound-toggle">{muted ? <IoVolumeMute size={18} /> : <IoVolumeHigh size={18} />}</button>
            <button style={styles.gear} onClick={() => nav("/parametres")} title="Paramètres"><IoSettingsOutline size={18} /></button>
            <button style={styles.hubBtn} onClick={() => nav("/hub")}>Hub</button>
            {isAuthenticated ? (
              <button style={styles.avatarBtn} data-testid="menu-avatar" onClick={() => nav("/profil")} title={user?.name || user?.email}><Avatar size={38} /></button>
            ) : (
              <button style={styles.account} onClick={() => nav("/connexion")}>Connexion</button>
            )}
          </div>
        )}
      </header>

      {/* fil d'ariane mobile (sous le header) */}
      {!bare && isMobile && crumbs.length > 0 && (
        <div style={styles.mobileCrumbs}>
          <button style={styles.crumbHome} onClick={() => nav("/hub")}>Hub</button>
          {crumbs.map((c, i) => (
            <span key={i} style={styles.crumbItem}>
              <span style={styles.sep}>›</span>
              {c.to ? <button style={styles.crumbLink} onClick={() => nav(c.to)}>{c.label}</button> : <span style={styles.crumbCurrent}>{c.label}</span>}
            </span>
          ))}
        </div>
      )}

      {/* Drawer mobile */}
      {isMobile && menuOpen && (
        <div style={styles.drawerOverlay} onClick={() => setMenuOpen(false)} data-testid="mobile-menu-drawer">
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.drawerHead}>
              <span style={styles.brandText}>TENGOKU <span style={{ color: RED }}>DŌJŌ</span></span>
              <button style={styles.drawerClose} onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close"><IoClose size={22} /></button>
            </div>
            {user && (
              <button style={styles.drawerProfile} data-testid="drawer-profile" onClick={() => go("/profil")}>
                <Avatar size={44} />
                <div style={{ textAlign: "left", flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: 15 }}>{user.name || "Mon profil"}</div>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>Voir / modifier mon profil ›</div>
                </div>
              </button>
            )}
            <nav style={styles.drawerNav}>
              {NAV.map((n) => (
                <button key={n.label} style={{ ...styles.drawerLink, position: "relative" }} data-testid={`mobile-nav-${n.label.toLowerCase()}`} onClick={n.action}>{n.label}{NAV_SECTION[n.label] && unseen(NAV_SECTION[n.label]) && <Dot />}{n.label === "Relectures" && pending > 0 && <Dot />}{n.label === "Nouveautés" && patchUnread > 0 && <Dot />}</button>
              ))}
            </nav>
            <div style={styles.drawerFoot}>
              <div style={{ display: "flex", justifyContent: "center" }}><TextSizeButton btnStyle={{ ...styles.gear, padding: "6px 12px", width: "auto" }} dropUp /></div>
              {isAuthenticated ? (
                <button style={styles.drawerLogout} onClick={async () => { setMenuOpen(false); await logout(); nav("/connexion"); }}>Se déconnecter</button>
              ) : (
                <button style={styles.drawerLogout} onClick={() => go("/connexion")}>Connexion</button>
              )}
            </div>
          </div>
        </div>
      )}

      <main style={{ ...styles.main, ...(isMobile ? { paddingBottom: 96 } : {}) }}>{children}</main>

      {/* Barre de navigation basse fixe (mobile) — style app native */}
      {isMobile && (
        <nav style={styles.bottomNav} data-testid="mobile-bottom-nav">
          {[
            { label: "Hub", to: "/hub", icon: IoHomeOutline, match: (pn) => pn === "/hub" },
            { label: "École", to: "/ecole", icon: IoSchoolOutline, match: (pn) => pn.startsWith("/ecole"), sec: "ecole" },
            { label: "Biblio", to: "/bibliotheque", icon: IoLibraryOutline, match: (pn) => pn.startsWith("/bibliotheque"), sec: "biblio" },
            { label: "Carnet", to: "/carnet", icon: IoJournalOutline, match: (pn) => pn.startsWith("/carnet") && !pn.startsWith("/carnet/eleve") },
            { label: "Coaching", to: "/accompagnement", icon: IoSparklesOutline, match: (pn) => pn.startsWith("/accompagnement"), sec: "accompagnement" },
            { label: "Communauté", action: () => nav("/communaute"), icon: IoChatbubblesOutline, match: (pn) => pn.startsWith("/communaute"), sec: "communaute" },
          ].map((it) => {
            const active = it.match(loc.pathname);
            const Icon = it.icon;
            return (
              <button key={it.label} data-testid={`bottomnav-${it.label.toLowerCase()}`}
                onClick={() => it.action ? it.action() : nav(it.to)}
                style={{ ...styles.bnItem, ...(active ? styles.bnItemActive : {}), position: "relative" }}>
                {it.sec && unseen(it.sec) && <span style={{ ...styles.newsDot, top: 6, right: "50%", marginRight: -16 }} data-testid="news-dot" />}
                <Icon size={22} />
                <span style={styles.bnLabel}>{it.label}</span>
              </button>
            );
          })}
        </nav>
      )}
      <NerodusGuide />
      <AccessRequestPrompt />
      <ReviewAlertPrompt />
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)", color: "var(--text-primary)", fontFamily: "var(--font-body, 'Inter', system-ui, sans-serif)", position: "relative", overflow: "hidden" },
  bgKanji: { position: "fixed", right: "-4vw", bottom: "-6vh", fontSize: "46vh", color: "var(--bg-secondary)", opacity: 0.5, fontWeight: 800, pointerEvents: "none", userSelect: "none", zIndex: 0, lineHeight: 1 },
  header: { position: "sticky", top: 0, zIndex: 20, display: "flex", alignItems: "center", gap: 18, padding: "14px 20px", background: "color-mix(in srgb, var(--bg-primary) 88%, transparent)", backdropFilter: "blur(8px)", borderBottom: "1px solid var(--border-c)" },
  brand: { display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 },
  kanji: { fontSize: 26, fontWeight: 700, color: "var(--text-primary)" },
  brandText: { fontSize: 15, fontWeight: 800, letterSpacing: 2, color: "var(--text-primary)" },
  crumbs: { display: "flex", alignItems: "center", gap: 4, flex: 1, marginLeft: 10, flexWrap: "wrap" },
  crumbHome: { background: "none", border: "none", color: RED, fontWeight: 700, cursor: "pointer", fontSize: 13.5 },
  crumbItem: { display: "flex", alignItems: "center", gap: 4 },
  sep: { color: "var(--text-secondary)" },
  crumbLink: { background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: 13.5, fontWeight: 600 },
  crumbCurrent: { color: "var(--text-primary)", fontSize: 13.5, fontWeight: 700 },
  right: { display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" },
  hubBtn: { padding: "8px 16px", borderRadius: 9, border: "none", background: RED, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13, boxShadow: `0 4px 12px color-mix(in srgb, var(--brand) 27%, transparent)` },
  gear: { width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 9, border: "1.5px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-primary)", cursor: "pointer" },
  navLink: { padding: "8px 12px", borderRadius: 9, border: "none", background: "transparent", color: "var(--text-secondary)", fontWeight: 700, cursor: "pointer", fontSize: 13 },
  account: { padding: "8px 14px", borderRadius: 9, border: "1.5px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-primary)", fontWeight: 600, cursor: "pointer", fontSize: 13 },
  avatarBtn: { padding: 0, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center" },
  drawerProfile: { display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "12px", borderRadius: 14, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", cursor: "pointer", marginBottom: 14 },
  burger: { marginLeft: "auto", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10, border: "1.5px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-primary)", cursor: "pointer" },
  menuBtn: { display: "inline-flex", alignItems: "center", gap: 6, height: 40, padding: "0 14px", borderRadius: 999, border: "1.5px solid var(--brand)", background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: "var(--brand)", fontWeight: 800, fontSize: 14, cursor: "pointer" },
  menuBtnLabel: { fontSize: 14, fontWeight: 800 },
  mobileCrumbs: { position: "relative", zIndex: 5, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", padding: "8px 20px", borderBottom: "1px solid var(--border-c)", background: "var(--bg-secondary)" },
  drawerOverlay: { position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(3px)", display: "flex", justifyContent: "flex-end" },
  drawer: { width: "min(80vw, 320px)", height: "100%", background: "var(--bg-primary)", borderLeft: "1px solid var(--border-c)", display: "flex", flexDirection: "column", padding: 20, boxShadow: "-10px 0 40px rgba(0,0,0,0.3)", animation: "tdDrawerIn .22s ease" },
  drawerHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 },
  drawerClose: { width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-primary)", cursor: "pointer" },
  drawerUser: { fontSize: 13, color: "var(--text-secondary)", fontWeight: 600, padding: "8px 12px", borderRadius: 10, background: "var(--bg-secondary)", marginBottom: 14 },
  drawerNav: { display: "flex", flexDirection: "column", gap: 6, flex: 1 },
  drawerLink: { textAlign: "left", padding: "14px 16px", borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 16, cursor: "pointer" },
  drawerFoot: { display: "flex", flexDirection: "column", gap: 12, paddingTop: 14, borderTop: "1px solid var(--border-c)", marginTop: 14 },
  drawerLogout: { padding: "13px 16px", borderRadius: 12, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer" },
  main: { position: "relative", zIndex: 1, maxWidth: 1120, margin: "0 auto", padding: "28px 20px 90px" },
  bottomNav: { position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 55, display: "flex", justifyContent: "space-around", alignItems: "stretch", background: "var(--bg-primary)", borderTop: "1px solid var(--border-c)", boxShadow: "0 -6px 24px rgba(0,0,0,0.12)", paddingBottom: "env(safe-area-inset-bottom, 0px)" },
  bnItem: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, padding: "9px 4px 10px", border: "none", background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontWeight: 700 },
  bnItemActive: { color: "var(--brand)" },
  bnLabel: { fontSize: 11, fontWeight: 700 },
  newsDot: { position: "absolute", top: 4, right: 4, width: 9, height: 9, borderRadius: "50%", background: "var(--brand)", border: "1.5px solid var(--bg-primary)", boxShadow: "0 0 0 2px color-mix(in srgb, var(--brand) 30%, transparent)" },
};

if (typeof document !== "undefined" && !document.getElementById("dojo-drawer-kf")) {
  const el = document.createElement("style");
  el.id = "dojo-drawer-kf";
  el.textContent = "@keyframes tdDrawerIn{from{transform:translateX(100%)}to{transform:translateX(0)}}";
  document.head.appendChild(el);
}
